/**
 * backgroundTaskStore — module-scope (not React-state) tracker for a single
 * long-running background task, e.g. the Sales Invoice → SharePoint import.
 *
 * Lives outside any lazy-loaded module so the Shell/Sidebar (always mounted)
 * can show progress regardless of which module route is currently active,
 * and so navigating away and back doesn't lose track of an in-flight task —
 * the task itself is plain async code that keeps running on unmount; this
 * store just gives any mounted component a way to observe it.
 */
export interface BackgroundTask {
  label: string;
  done: number;
  total: number;
}

type Listener = (task: BackgroundTask | null) => void;

let current: BackgroundTask | null = null;
const listeners = new Set<Listener>();

export function setBackgroundTask(task: BackgroundTask | null): void {
  current = task;
  listeners.forEach((l) => l(current));
}

export function getBackgroundTask(): BackgroundTask | null {
  return current;
}

export function subscribeBackgroundTask(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
